import React, {
  memo, useCallback, useState,
} from 'react'

import { string } from 'prop-types'

import CodeStep from 'Pods/LazyLoaded/AiSignInModal/Components/CodeStep/CodeStep'
import { CODE_LENGTH } from 'Pods/LazyLoaded/AiSignInModal/constants'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import Modal from 'Pods/Shared/Modal/Modal'

import { NOOP } from 'Utils/stableDefaults'

import EmailForm from './Components/EmailForm/EmailForm'
import Intro from './Components/Intro/Intro'
import ModelGallery from './Components/ModelGallery/ModelGallery'
import { CONFERENCE_PATH, EMAIL_REGEX, ERROR_MESSAGES } from './constants'

const VERIFY_PATH = '/guests/sessions/conference/verify'
const EMPTY_CODE = Array.from({ length: CODE_LENGTH }, () => '')

const STEP = {
  EMAIL: 'email',
  CODE:  'code',
}

function ConferenceSignInModal({ eventName }) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  const [step, setStep] = useState(STEP.EMAIL)
  const [digits, setDigits] = useState(EMPTY_CODE)
  const [hasCodeError, setHasCodeError] = useState(false)
  const { postData, isLoading: isSubmitting } = useApiPost()

  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value)
    if (error) setError(null)
  }, [error])

  // Stash the event in localStorage with a 20-min TTL (matches the BE conference session
  // TTL) so the XHR logout in UnifiedNavigation can recover it and bring the next booth
  // visitor back to /ai?conference=<event>.
  const persistEventForLogoutReturn = useCallback(() => {
    try {
      localStorage.setItem(
        'conferenceEvent',
        JSON.stringify({ event: eventName, expiresAt: Date.now() + (20 * 60 * 1000) })
      )
    } catch (_) { /* localStorage disabled — fall back to plain reload */ }
  }, [eventName])

  const reloadStrippingConferenceParam = useCallback(() => {
    persistEventForLogoutReturn()
    const url = new URL(window.location.href)
    url.searchParams.delete('conference')
    window.location.replace(url.toString())
  }, [persistEventForLogoutReturn])

  const handleSubmit = useCallback(() => {
    const trimmed = email.trim()
    if (!EMAIL_REGEX.test(trimmed)) {
      setError(ERROR_MESSAGES.invalid_email)
      return
    }
    if (isSubmitting) return

    setError(null)

    postData({
      path: CONFERENCE_PATH,
      data: { email: trimmed, event_name: eventName },
      onSuccess: (response) => {
        if (response?.data?.success) {
          reloadStrippingConferenceParam()
          return
        }
        if (response?.data?.needs_code) {
          setDigits(EMPTY_CODE)
          setHasCodeError(false)
          setStep(STEP.CODE)
          return
        }
        setError(ERROR_MESSAGES.generic)
      },
      onError: (err) => {
        if (err?.response?.status === 404) {
          setError(ERROR_MESSAGES.not_found)
        } else if (err?.response?.data?.error === 'invalid_params') {
          setError(ERROR_MESSAGES.invalid_email)
        } else {
          setError(ERROR_MESSAGES.generic)
        }
      },
    })
  }, [email, eventName, isSubmitting, postData, reloadStrippingConferenceParam])

  const handleDigitsChange = useCallback((next) => {
    setHasCodeError(false)
    setDigits(next)

    if (!next.every(Boolean)) return

    postData({
      path: VERIFY_PATH,
      data: { email: email.trim(), code: next.join(''), event_name: eventName },
      onSuccess: (response) => {
        if (response?.data?.success) {
          reloadStrippingConferenceParam()
        } else {
          setHasCodeError(true)
        }
      },
      onError: () => { setHasCodeError(true) },
    })
  }, [email, eventName, postData, reloadStrippingConferenceParam])

  const handleResend = useCallback(() => {
    setDigits(EMPTY_CODE)
    setHasCodeError(false)
    postData({
      path: CONFERENCE_PATH,
      data: { email: email.trim(), event_name: eventName },
    })
  }, [email, eventName, postData])

  return (
    <Modal
      isOpen
      width="medium"
      customClass="conference-sign-in-modal"
      ariaLabel="Sign in for free trial"
      canClose={false}
      onClose={NOOP}
    >
      <div className="conference-sign-in-modal__body">
        <div className="conference-sign-in-modal__main">
          {step === STEP.EMAIL && (
            <>
              <Intro />
              <EmailForm
                email={email}
                error={error}
                needsSignIn={false}
                isSubmitting={isSubmitting}
                onEmailChange={handleEmailChange}
                onSubmit={handleSubmit}
              />
            </>
          )}
          {step === STEP.CODE && (
            <CodeStep
              email={email}
              digits={digits}
              hasError={hasCodeError}
              onDigitsChange={handleDigitsChange}
              onResend={handleResend}
            />
          )}
        </div>

        <ModelGallery />
      </div>
    </Modal>
  )
}

ConferenceSignInModal.propTypes = {
  eventName: string.isRequired,
}

export default memo(ConferenceSignInModal)
